/**
 * Composable pour accéder à la configuration de contenu selon le type d'utilisateur
 * Pour le contenu de la top bar
 *
 * @module layouts/composables/useLayout
 */

import { computed } from 'vue';
import { useUser } from '@/common/composables/useUser';
import { useDeviceDetection } from '@/common/composables/useDeviceDetection';
import { USER_CONTENT, DEFAULT_USER_CONTENT, type UserContentConfig } from '@/layouts/constants';
import { UserType } from '@/common/types/userTypes';
import { USER_TYPE } from '@/common/constants';

/**
 * Composable pour accéder à la configuration de contenu
 *
 * @returns Configuration de contenu selon le type d'utilisateur
 *
 */
export function useLayout() {
  const { userType } = useUser();
  const { layoutContext } = useDeviceDetection();

  // ============================================
  // Configuration de contenu
  // ============================================

  /**
   * Configuration de contenu selon le type d'utilisateur
   */
  const userContent = computed<UserContentConfig>(() => {
    const type = userType.value ?? USER_TYPE.STUDENT;
    return USER_CONTENT[type as UserType] ?? DEFAULT_USER_CONTENT;
  });

  // ============================================
  // Raccourcis Contenu (utilisés par TopBar et composants de contenu)
  // ============================================

  /** Afficher la barre de recherche rapide */
  const shouldShowQuickSearch = computed(() => userContent.value.showQuickSearch);

  /** Afficher les outils (notifications, etc.) */
  const shouldShowTools = computed(() => userContent.value.showTools);

  /** Afficher le bouton Chat */
  const shouldShowChat = computed(() => userContent.value.showChat);

  /** Afficher le bouton Chatbot (Agent IA) */
  const shouldShowChatbot = computed(() => userContent.value.showChatbot);

  /** Routes à exclure des menus */
  const excludedRouteNames = computed(() => userContent.value.excludedRouteNames);

  // ============================================
  // Return
  // ============================================

  return {
    // Contexte
    userType,
    layoutContext,

    // Configuration complète
    userContent,

    // Raccourcis Contenu
    shouldShowQuickSearch,
    shouldShowTools,
    shouldShowChat,
    shouldShowChatbot,
    excludedRouteNames,
  };
}
